import { Suspense, useEffect, useRef } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import routes, { RouteType } from './route'
import ProtectedRoute from './ProtectedRoute'
import { PageLoader } from '@/components'
import { snapshotPendingGets } from '@/lib/axios'

// Abort the leaving page's in-flight GET reads when the route changes. Snapshot
// happens during render — before the new page's queries fire in mount effects —
// so only the abandoned page's requests get cancelled, never the incoming one's.
function useCancelStaleRequestsOnNavigate() {
  const { pathname } = useLocation()
  const prev = useRef(pathname)
  const stale = useRef<AbortController[]>([])

  if (prev.current !== pathname) {
    stale.current = snapshotPendingGets()
    prev.current = pathname
  }

  useEffect(() => {
    stale.current.forEach((controller) => controller.abort())
    stale.current = []
  }, [pathname])
}

const AppRoutes = () => {
  useCancelStaleRequestsOnNavigate()
  const { pathname } = useLocation()
  return (
    // React Router v7 wraps navigation in startTransition, so when the next
    // route's lazy chunk suspends, React keeps the previous page on screen
    // instead of showing a fallback — the old page appears "stuck" (QTY-1856).
    // Keying the boundary by pathname marks each route as different content,
    // which makes React show the loader immediately on navigation.
    <Suspense key={pathname} fallback={<PageLoader />}>
      <Routes>
        {/* Legacy URL: the assign-branch wizard was replaced by the Branch Access page */}
        <Route
          path="/staff/assign-branch"
          element={<Navigate to="/staff/branch-access" replace />}
        />
        {routes.map((route) => {
          const Component = route.component
          const isPrivate = (route.route_type as RouteType) === RouteType.PRIVATE
          const element = isPrivate ? (
            <ProtectedRoute>
              <Component />
            </ProtectedRoute>
          ) : (
            <Component />
          )
          return <Route key={route.path} path={route.path} element={element} />
        })}
        <Route path="/" element={<Navigate to="/login" replace />} />
      </Routes>
    </Suspense>
  )
}

export default AppRoutes
